import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { AccountReceivable } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { PaymentModal } from "../../components/PaymentModal";
import { ReversePaymentModal } from "../../components/ReversePaymentModal";
import { useAuth } from "../../auth/AuthContext";
import { getCurrentMonthRange } from "../../utils/dateUtils";
import { formatDateBR, formatMoneyBR } from "../../utils/format";
import { OPERATION_NATURE_PT, RECEIVABLE_STATUS_PT } from "../../constants/enumsPt";
import { formatDateInput } from "../../utils/dateRange";
import { PeriodNavigator } from "../../components/PeriodNavigator";

export const ReceivablesPage: React.FC = () => {
  const { client } = useAuth();
  const [paymentModal, setPaymentModal] = useState<{
    show: boolean;
    itemId: number;
    itemDescription: string;
    itemAmount: number;
    itemDueDate: string;
  }>({
    show: false,
    itemId: 0,
    itemDescription: "",
    itemAmount: 0,
    itemDueDate: "",
  });
  const [reverseModal, setReverseModal] = useState<{
    show: boolean;
    itemId: number;
    itemDescription: string;
  }>({
    show: false,
    itemId: 0,
    itemDescription: "",
  });
  const [refreshList, setRefreshList] = useState<null | (() => Promise<void>)>(
    null
  );
  const currentRange = useMemo(() => getCurrentMonthRange(), []);
  const [statusFilter, setStatusFilter] = useState("OPEN");
  const [fromDate, setFromDate] = useState(currentRange.from);
  const [toDate, setToDate] = useState(currentRange.to);

  const listFilters = useMemo(
    () => ({
      ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
      ...(fromDate ? { from: fromDate } : {}),
      ...(toDate ? { to: toDate } : {}),
    }),
    [fromDate, statusFilter, toDate]
  );

  const isIncomplete = (item: AccountReceivable) =>
    !item.accountId ||
    !item.costCenterId ||
    !item.categoryId ||
    !item.bankAccountId ||
    !item.clientId;

  const fetchClients = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/clients?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; entity: { name: string } }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: item.entity.name,
      }));
    },
    [client]
  );

  const fetchCategoryOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/categories?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    return payload.data.map((item) => ({ id: item.id, label: item.name }));
  }, [client]);

  const fetchCostCenterOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/cost-centers?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; code?: string | null; name: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.code ?? ""} ${item.name}`.trim(),
    }));
  }, [client]);

  const fetchBankAccountOptions = useCallback(async (query: string) => {
    void query;
    const response = await client.get(`/modules/bank-accounts?take=200&skip=0`);
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.bank.name} ${item.agency}/${item.accountNumber}`,
    }));
  }, [client]);

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const toDateInput = useCallback((value: string | number | boolean | null) => {
    if (!value || typeof value !== "string") {
      return "";
    }
    return value.length >= 10 ? value.slice(0, 10) : value;
  }, []);

  const handlePeriodChange = useCallback((from: string, to: string) => {
    setFromDate(from ? formatDateInput(new Date(from)) : from);
    setToDate(to ? formatDateInput(new Date(to)) : to);
  }, []);

  const ReceivableFormExtras: React.FC<{
    values: Record<string, string | number | boolean | null>;
    setValues: React.Dispatch<
      React.SetStateAction<Record<string, string | number | boolean | null>>
    >;
    editingId: number | null;
  }> = ({ values, setValues, editingId }) => {
    useEffect(() => {
      if (!editingId) {
        return;
      }
      const normalized = toDateInput(values.dueDate);
      if (normalized && normalized !== values.dueDate) {
        setValues((prev) => ({ ...prev, dueDate: normalized }));
      }
    }, [editingId, setValues, toDateInput, values.dueDate]);

    return null;
  };

  return (
    <>
      <CrudPage<AccountReceivable>
        title="Contas a receber"
        endpoint="/modules/receivables"
        searchPlaceholder="Buscar por descrição ou cliente..."
        listFilters={listFilters}
        renderFilters={
          <div
            className="d-flex flex-wrap align-items-end gap-3 px-3 py-2 mb-2 rounded"
            style={{ background: "#f8f9fa", borderTop: "3px solid #0d6efd" }}
          >
            <small className="text-muted fw-semibold me-1 align-self-center">Filtros</small>
            <div>
              <label className="form-label mb-1 small fw-semibold">Status</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 140 }}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="ALL">Todos</option>
                <option value="OPEN">Em aberto</option>
                <option value="RECEIVED">Recebido</option>
              </select>
            </div>
            <div>
              <label className="form-label mb-1 small fw-semibold">De</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>
            <div>
              <label className="form-label mb-1 small fw-semibold">Até</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>
            <div className="align-self-end">
              <PeriodNavigator
                fromDate={fromDate}
                toDate={toDate}
                onChange={handlePeriodChange}
              />
            </div>
          </div>
        }
        columns={[
          {
            label: "Descrição / Cliente",
            render: (item) => {
              const incomplete = isIncomplete(item);
              const rec = item as AccountReceivable & { client?: { entity?: { name: string } | null } | null };
              return (
                <div>
                  <div className="d-flex align-items-center gap-1">
                    {incomplete && (
                      <span
                        className="badge text-bg-warning text-dark"
                        style={{ fontSize: "0.65rem" }}
                        title="Dados incompletos"
                      >!</span>
                    )}
                    <span className="fw-semibold">{item.description}</span>
                  </div>
                  {rec?.client?.entity?.name && (
                    <div className="text-muted small">{rec.client.entity.name}</div>
                  )}
                </div>
              );
            },
          },
          {
            label: "Valor",
            render: (item) => <span className="fw-semibold">{formatMoneyBR(item.amount)}</span>,
          },
          { label: "Vencimento", render: (item) => formatDateBR(item.dueDate) },
          {
            label: "Natureza",
            render: (item) => {
              const n = item.operationNature ?? "";
              const label = (OPERATION_NATURE_PT[n] ?? n) || "-";
              const colors: Record<string, string> = {
                OPEX: "text-bg-primary",
                CAPEX: "text-bg-warning text-dark",
                FINANCE: "text-bg-info",
                OTHER: "text-bg-secondary",
              };
              return (
                <span className={`badge ${colors[n] ?? "text-bg-secondary"}`} style={{ fontSize: "0.7rem" }}>
                  {label}
                </span>
              );
            },
          },
          {
            label: "Status",
            render: (item) => {
              const label = RECEIVABLE_STATUS_PT[item.status] ?? item.status ?? "-";
              const colors: Record<string, string> = {
                OPEN: "text-bg-warning text-dark",
                RECEIVED: "text-bg-success",
                PARTIAL: "text-bg-info",
                CANCELED: "text-bg-secondary",
                OVERDUE: "text-bg-danger",
              };
              return (
                <span className={`badge ${colors[item.status] ?? "text-bg-secondary"}`} style={{ fontSize: "0.7rem" }}>
                  {label}
                </span>
              );
            },
          },
        ]}
        customActions={(item, refresh) => (
          <>
            {item.status !== "RECEIVED" && item.status !== "CANCELED" && (
              <button
                className="btn btn-sm btn-success me-2"
                onClick={() => {
                  setRefreshList(() => refresh);
                  setPaymentModal({
                    show: true,
                    itemId: item.id,
                    itemDescription: item.description,
                    itemAmount: Number(item.amount),
                    itemDueDate: item.dueDate,
                  });
                }}
              >
                Receber
              </button>
            )}
            {item.status === "RECEIVED" && (
              <button
                className="btn btn-sm btn-outline-warning me-2"
                onClick={() => {
                  setRefreshList(() => refresh);
                  setReverseModal({
                    show: true,
                    itemId: item.id,
                    itemDescription: item.description,
                  });
                }}
              >
                Estornar
              </button>
            )}
          </>
        )}
        formFields={[
          {
            name: "clientId",
            label: "Cliente",
            type: "autocomplete",
            placeholder: "Buscar cliente",
            fetchOptions: fetchClients,
            getIdFromItem: (item) => item.clientId ?? null,
            getLabelFromItem: (item) => item.client?.entity?.name || "",
            required: true,
          },
          {
            name: "categoryId",
            label: "Categoria",
            type: "select",
            fetchOptions: fetchCategoryOptions,
            required: true,
          },
          {
            name: "costCenterId",
            label: "Centro de custo",
            type: "select",
            fetchOptions: fetchCostCenterOptions,
            required: true,
          },
          {
            name: "bankAccountId",
            label: "Conta bancária",
            type: "select",
            fetchOptions: fetchBankAccountOptions,
            required: true,
          },
          {
            name: "accountId",
            label: "Conta contábil",
            type: "autocomplete",
            placeholder: "Buscar conta contabil",
            fetchOptions: fetchAccounts,
            getIdFromItem: (item) => item.accountId ?? null,
            getLabelFromItem: (item) =>
              item.account ? `${item.account.code} - ${item.account.name}` : "",
            required: true,
          },
          { name: "description", label: "Descrição", type: "text", required: true },
          {
            name: "amount",
            label: "Valor",
            type: "text",
            required: true,
            displayValue: (value) => formatMoneyBR(Number(value ?? 0)),
          },
          {
            name: "dueDate",
            label: "Vencimento",
            type: "date",
            required: true,
            displayValue: (value) => formatDateBR(String(value ?? "")),
          },
        ]}
        initialValues={{
          clientId: null,
          categoryId: null,
          costCenterId: null,
          bankAccountId: null,
          accountId: null,
          description: "",
          amount: "",
          dueDate: "",
        }}
        mapToPayload={(values) => ({
          clientId: values.clientId ? Number(values.clientId) : null,
          categoryId: values.categoryId ? Number(values.categoryId) : null,
          costCenterId: values.costCenterId ? Number(values.costCenterId) : null,
          bankAccountId: values.bankAccountId ? Number(values.bankAccountId) : null,
          accountId: values.accountId ? Number(values.accountId) : null,
          description: values.description,
          amount: Number(values.amount),
          dueDate: values.dueDate,
        })}
        renderFormExtras={({ values, setValues, editingId }) => (
          <ReceivableFormExtras
            values={values}
            setValues={setValues}
            editingId={editingId}
          />
        )}
        permissionWrite="RECEIVABLE_WRITE"
      />
      <PaymentModal
        show={paymentModal.show}
        onClose={() => setPaymentModal({ ...paymentModal, show: false })}
        onSuccess={() => {
          setPaymentModal({ ...paymentModal, show: false });
          refreshList?.();
        }}
        itemId={paymentModal.itemId}
        itemType="receivable"
        itemDescription={paymentModal.itemDescription}
        itemAmount={paymentModal.itemAmount}
        itemDueDate={paymentModal.itemDueDate}
      />
      <ReversePaymentModal
        show={reverseModal.show}
        onClose={() => setReverseModal({ ...reverseModal, show: false })}
        onSuccess={() => {
          setReverseModal({ ...reverseModal, show: false });
          refreshList?.();
        }}
        itemId={reverseModal.itemId}
        itemDescription={reverseModal.itemDescription}
        itemType="receivable"
      />
    </>
  );
};
